/**
 * 飞书认证 React Hook
 *
 * 增强：token 过期自动清除、错误分类
 */
import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import {
  FeishuAuth,
  AuthError,
  consumeOAuthRestoreHash,
  type FeishuAuthConfig,
  type FeishuUser,
  type AuthErrorType,
} from '../auth'
import { isViteDevAuthBypassEnabled } from '../devBypass'

const TOKEN_MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours
const NONCE_RECOVERY_ONCE_KEY = 'cnkis_nonce_recovery_once'

interface UseFeishuAuthReturn {
  user: FeishuUser | null
  token: string | null
  loading: boolean
  error: string | null
  errorType: AuthErrorType | null
  login: () => void
  logout: () => void
  isAuthenticated: boolean
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) return null
  return value as Record<string, unknown>
}

function normalizeFeishuUser(raw: unknown): FeishuUser | null {
  const source = asRecord(raw)
  if (!source) return null
  const payload = asRecord(source.data) ?? source
  const account = asRecord(payload.account) ?? asRecord(payload.user) ?? payload

  const id =
    account.id
    ?? account.open_id
    ?? account.user_id
    ?? account.union_id
    ?? (typeof account.name === 'string' ? account.name : undefined)
  let name =
    typeof account.name === 'string'
      ? account.name
      : typeof account.display_name === 'string'
        ? account.display_name
        : typeof account.username === 'string'
          ? account.username
          : ''
  const emailRaw = typeof account.email === 'string' ? account.email.trim() : ''
  if (!name.trim() && emailRaw) {
    name = emailRaw.split('@')[0] || emailRaw
  }
  if (!name.trim() && (typeof id === 'string' || typeof id === 'number')) {
    name = `用户${String(id)}`
  }
  if (!name.trim() || (typeof id !== 'string' && typeof id !== 'number')) return null

  return {
    id,
    name: name.trim(),
    email: typeof account.email === 'string' ? account.email : '',
    avatar: typeof account.avatar === 'string' ? account.avatar : '',
    department: typeof account.department === 'string' ? account.department : '',
  }
}

function hasOAuthCodeInUrl(): boolean {
  if (typeof window === 'undefined') return false
  const query = new URLSearchParams(window.location.search)
  if (query.get('code')) return true
  if (!window.location.hash.includes('?')) return false
  const hashQuery = window.location.hash.split('?')[1] || ''
  const hashParams = new URLSearchParams(hashQuery)
  return !!hashParams.get('code')
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    // ignored
  }
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
    return
  } catch {
    // ignored
  }
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // ignored
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignored
  }
  try {
    sessionStorage.removeItem(key)
  } catch {
    // ignored
  }
}

function isTokenExpired(): boolean {
  try {
    const token = safeGetItem('auth_token')
    /** 开发旁路固定串；非 JWT，勿按 exp/ts 判过期，否则刷新后 useState 初值会误清 storage */
    if (token === 'dev-bypass-token') return false
    if (token) {
      const [, payloadBase64] = token.split('.')
      if (payloadBase64) {
        const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/')
        const payload = JSON.parse(atob(normalized))
        if (typeof payload.exp === 'number') {
          // 提前 5 分钟视为过期，减少临界态请求失败
          return Date.now() >= payload.exp * 1000 - 5 * 60 * 1000
        }
      }
    }
    const ts = safeGetItem('auth_token_ts')
    if (!ts) return false
    return Date.now() - Number(ts) > TOKEN_MAX_AGE_MS
  } catch {
    return false
  }
}

/** 供 FeishuAuthProvider 开发旁路退出等场景复用 */
export function clearAuthStorage() {
  safeRemoveItem('auth_token')
  safeRemoveItem('auth_user')
  safeRemoveItem('auth_profile')
  safeRemoveItem('auth_profile_token')
  safeRemoveItem('auth_roles')
  safeRemoveItem('auth_workbenches')
  safeRemoveItem('auth_token_ts')
  safeRemoveItem('auth_session_meta')
}

const DEV_BYPASS_USER_JSON = JSON.stringify({
  id: 0,
  name: '开发测试用户',
  email: 'dev@cnkis.local',
  avatar: '',
})

export function useFeishuAuth(config: FeishuAuthConfig): UseFeishuAuthReturn {
  const [user, setUser] = useState<FeishuUser | null>(() => {
    if (isViteDevAuthBypassEnabled()) {
      try {
        const u = JSON.parse(DEV_BYPASS_USER_JSON) as FeishuUser
        return u
      } catch {
        return null
      }
    }
    return null
  })
  const [token, setToken] = useState<string | null>(() => {
    if (isViteDevAuthBypassEnabled()) {
      safeSetItem('auth_token', 'dev-bypass-token')
      safeSetItem('auth_token_ts', String(Date.now()))
      safeSetItem('auth_user', DEV_BYPASS_USER_JSON)
      return 'dev-bypass-token'
    }
    const t = safeGetItem('auth_token')
    if (t && isTokenExpired()) {
      clearAuthStorage()
      return null
    }
    return t
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorType, setErrorType] = useState<AuthErrorType | null>(null)

  const auth = new FeishuAuth(config)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const onTokenClearedExternally = () => {
      setToken(null)
      setUser(null)
      setError(null)
      setErrorType(null)
      setLoading(false)
    }
    window.addEventListener('cnkis-auth-token-cleared', onTokenClearedExternally)
    return () => window.removeEventListener('cnkis-auth-token-cleared', onTokenClearedExternally)
  }, [])

  useEffect(() => {
    const init = async () => {
      if (isViteDevAuthBypassEnabled()) {
        safeSetItem('auth_token', 'dev-bypass-token')
        safeSetItem('auth_token_ts', String(Date.now()))
        safeSetItem('auth_user', DEV_BYPASS_USER_JSON)
        setToken('dev-bypass-token')
        try {
          setUser(JSON.parse(DEV_BYPASS_USER_JSON) as FeishuUser)
        } catch {
          setUser(null)
        }
        setError(null)
        setErrorType(null)
        setLoading(false)
        return
      }

      // If token exists but expired, clear it
      if (token && isTokenExpired()) {
        clearAuthStorage()
        setToken(null)
        setLoading(false)
        return
      }

      const savedUser = safeGetItem('auth_user')
      const hasOauthCode = hasOAuthCodeInUrl()
      if (token && savedUser && !hasOauthCode) {
        try {
          const normalizedUser = normalizeFeishuUser(JSON.parse(savedUser))
          if (!normalizedUser) {
            clearAuthStorage()
            setToken(null)
          } else {
            setUser(normalizedUser)
            setLoading(false)
            return
          }
        } catch {
          clearAuthStorage()
          setToken(null)
        }
      }

      /**
       * 仅有 JWT、无 auth_user 时 isAuthenticated 恒为 false，会回到登录页。
       * 常见于：换票后只写入了 token、或 storage 配额/隐私模式导致 auth_user 未写入。
       * 用 storage 再读一次，避免上一段 clear 后仍沿用闭包里的旧 savedUser 判断。
       */
      const apiBase = config.apiBaseUrl || '/api/v1'
      const memToken = safeGetItem('auth_token')
      const memUser = safeGetItem('auth_user')
      if (memToken && !memUser && !hasOauthCode) {
        try {
          const resp = await axios.get(`${apiBase}/auth/me`, {
            headers: { Authorization: `Bearer ${memToken}` },
            timeout: 15000,
          })
          const normalizedUser = normalizeFeishuUser(resp.data)
          if (normalizedUser) {
            setUser(normalizedUser)
            setToken(memToken)
            safeSetItem('auth_user', JSON.stringify(normalizedUser))
            if (!safeGetItem('auth_token_ts')) {
              safeSetItem('auth_token_ts', String(Date.now()))
            }
            setLoading(false)
            return
          }
          clearAuthStorage()
          setToken(null)
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status === 401) {
            clearAuthStorage()
            setToken(null)
          } else {
            const msg = axios.isAxiosError(err) && err.response?.status
              ? `无法验证登录状态（HTTP ${err.response.status}）`
              : '网络异常，无法验证登录状态'
            setError(msg)
            setErrorType(axios.isAxiosError(err) && !err.response ? 'network' : 'unknown')
            setLoading(false)
            return
          }
        }
      }

      try {
        const result = await auth.autoLogin()
        if (result) {
          safeRemoveItem(NONCE_RECOVERY_ONCE_KEY)
          const normalizedUser = normalizeFeishuUser(result.user)
          if (!normalizedUser) {
            throw new AuthError('用户信息结构异常', 'unknown')
          }
          // 先持久化再 setState，避免 effect 因 token 依赖立刻重跑时读到旧 storage
          safeSetItem('auth_token', result.token)
          safeSetItem('auth_user', JSON.stringify(normalizedUser))
          safeSetItem('auth_token_ts', String(Date.now()))
          if (result.roles) {
            safeSetItem('auth_roles', JSON.stringify(result.roles))
          }
          if (result.visible_workbenches) {
            safeSetItem('auth_workbenches', JSON.stringify(result.visible_workbenches))
          }
          if (result.session_meta) {
            safeSetItem('auth_session_meta', JSON.stringify(result.session_meta))
          }
          setToken(result.token)
          setUser(normalizedUser)
          const restoreHash = consumeOAuthRestoreHash()
          if (restoreHash) {
            window.location.replace(
              `${window.location.origin}${window.location.pathname}${window.location.search}${restoreHash}`,
            )
            return
          }
          // 不再整页 reload：reload 与并发请求/双 effect 叠加易重复消费 code 或触发 NONCE_REPLAY → 自动再跳飞书。
          // oauthCodeExchangePromise + 本地 storage 已足以让 Strict Mode 下第二次 autoLogin 命中同一换票结果。
          return
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : '登录失败'
        const type = err instanceof AuthError ? err.type : 'unknown'
        // 勿在 AUTH_NONCE_REPLAY 时自动 redirectToAuth：与双请求/Strict 模式叠加会形成「无人点击却反复进飞书」死循环；请用户点「飞书登录」或刷新后重试。
        if (msg.includes('AUTH_NONCE_REPLAY')) {
          safeRemoveItem(NONCE_RECOVERY_ONCE_KEY)
        }
        setError(msg)
        setErrorType(type)
      } finally {
        setLoading(false)
      }
    }

    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, config.apiBaseUrl])

  const login = useCallback(() => {
    auth.redirectToAuth()
  }, [auth])

  const logout = useCallback(() => {
    const currentToken = token
    if (currentToken) {
      axios.post(
        `${config.apiBaseUrl || '/api/v1'}/auth/logout`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } },
      ).catch(() => {
        // best-effort 撤销，失败时仍然本地清理
      })
    }
    setUser(null)
    setToken(null)
    setError(null)
    setErrorType(null)
    clearAuthStorage()
  }, [])

  return {
    user,
    token,
    loading,
    error,
    errorType,
    login,
    logout,
    isAuthenticated: !!token && !!user,
  }
}
